import React, { useState, useEffect, useRef } from 'react';
import {
  Euro, User, Calculator, FileText, Check, X, Eye, ChevronDown, ChevronRight,
  Clock, AlertTriangle, CheckCircle, Download, Send, Calendar,
  CreditCard, Printer, Settings, Plus, Search, Filter, GraduationCap,
  Building, ArrowLeft, FolderOpen, Briefcase, Shield, ChevronLeft, AlertCircle,
  Banknote, Edit2, Trash2, History, TrendingUp, DollarSign, Activity, PieChart, Info,
  HandCoins, Lock, ShieldAlert, ScrollText, Loader2
} from 'lucide-react';
import { api } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmDialog from './ConfirmDialog';
// Les fenêtres sont des Dialog (DESIGN-SYSTEM.md) : Radix les porte dans le
// body et gère l'empilement, là où les calques maison passaient sous l'en-tête.
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import AnnulationPrivilegieeDialog from './avances/AnnulationPrivilegieeDialog';
import JournalActionsPrivilegiees from './avances/JournalActionsPrivilegiees';
import { usePermissionsAnnulationPrivilegiee } from './avances/annulationPrivilegieeApi';
import { fenetreEffective, formaterDateLongue } from '../utils/fenetresPaie';
import { getAuthHeaders } from '../services/api';
import InfoBulle from './ui/InfoBulle';

// Note d'information sur le calendrier de paie, avec les dates reellement
// configurees pour l'annee scolaire et l'etat courant du mois selectionne.
// Infobulle des statistiques : qui a pris des avances dans la catégorie.
const BulleAvancesCategorie = ({ titre, personnes, messageVide }) => {
  const liste = Array.isArray(personnes) ? personnes : [];
  const formater = (valeur) => new Intl.NumberFormat('fr-FR').format(Math.round(Number(valeur || 0)));

  return (
    <InfoBulle
      titre={liste.length ? `${titre} : ${liste.length} personne${liste.length > 1 ? 's' : ''}` : titre}
      cote="right"
      alignement="start"
      libelle={`Voir les avances : ${titre}`}
    >
      {liste.length === 0 ? (
        <p>{messageVide}</p>
      ) : (
        <ul className="max-h-60 divide-y divide-gray-100 overflow-y-auto">
          {liste.map((personne, index) => {
            const nombre = Number(personne.nombre_avances || 0);
            return (
              <li key={`${personne.nom_complet}-${index}`} className="flex items-baseline justify-between gap-3 py-1.5">
                <span className="min-w-0 truncate text-gray-800">{personne.nom_complet}</span>
                <span className="flex-shrink-0 text-right">
                  <span className="font-semibold text-gray-900">{formater(personne.montant_total)} FCFA</span>
                  <span className="ml-1 text-gray-500">({nombre} avance{nombre > 1 ? 's' : ''})</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </InfoBulle>
  );
};

// Clé « année scolaire | année civile | mois civil » du mois dont on lit l'état
// figé : celle du mois de paie choisi (son début), sinon celle de la date.
const moisFigeCle = (moisPaie, date, anneeScolaireId) => {
  const debut = String(moisPaie?.date_debut || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(debut)) {
    const [annee, mois] = debut.split('-').map(Number);
    return `${anneeScolaireId || ''}|${annee}|${mois}`;
  }
  return `${anneeScolaireId || ''}|${date.getFullYear()}|${date.getMonth() + 1}`;
};

const NoteCalendrierPaie = ({
  parametres, moisFige, fenetreOuverte, horsCalendrier, periodeAvances, ouvertureAvances,
  enLecture = false, erreurLecture = '', libelleDateAffichee = ''
}) => {
  const debut = Number(parametres?.avance_jour_debut || 0);
  const fin = Number(parametres?.avance_jour_fin || 0);
  const fenetreConfiguree = Boolean(debut && fin);
  // Les vraies dates du mois (week-ends reportés), sinon les numéros de jour.
  const periode = periodeAvances || `du ${debut} au ${fin} du mois`;

  // Tant que le calendrier, les paramètres, l'état figé et l'ouverture du mois
  // ne sont pas tous arrivés, on ne montre aucun état : comme le Portefeuille.
  if (erreurLecture) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
        <p className="text-sm text-red-800">{erreurLecture}</p>
      </div>
    );
  }

  if (enLecture) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
        <p className="text-sm text-gray-600">Lecture du calendrier de paie…</p>
      </div>
    );
  }

  // La date de l'en-tête ne tombe dans aucun mois de paie (septembre pour une
  // école d'octobre à juin) : une simple consigne, le mois se choisit plus bas.
  if (horsCalendrier) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <Calendar className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" />
        <p className="text-sm text-gray-700">
          {libelleDateAffichee ? <span className="capitalize">{libelleDateAffichee}</span> : 'Cette date'} n'appartient à
          aucun mois de paie. Choisissez le <strong>mois concerné</strong> dans la grille pour voir et créer les avances.
        </p>
      </div>
    );
  }

  if (moisFige) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
        <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600" />
        <div className="text-sm text-orange-900">
          <p className="font-semibold">Mois figé</p>
          <p className="mt-0.5 leading-relaxed">
            L'état des salaires de ce mois a été validé
            {moisFige.validated_by_nom ? ` par ${moisFige.validated_by_nom}` : ''} : la dotation a
            déjà été calibrée sur le net à payer arrêté. Aucune nouvelle avance ne peut plus y être
            imputée. Pour rouvrir ce mois, un administrateur doit le défiger depuis les paramètres
            de salaires.
          </p>
        </div>
      </div>
    );
  }

  if (fenetreConfiguree && !fenetreOuverte) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
        <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600" />
        <div className="text-sm text-orange-900">
          <p className="font-semibold">Période d'avances fermée</p>
          <p className="mt-0.5 leading-relaxed">
            Les avances ne sont autorisées que <strong>{periode}</strong>. Hors de ces dates, le
            système bloque la création d'une avance : le cycle passe en préparation de l'état des
            salaires, pour que le net à payer puisse être arrêté, puis validé. L'annulation d'une
            avance existante reste possible tant que le mois n'est pas figé.
          </p>
        </div>
      </div>
    );
  }

  // Ouverture exceptionnelle accordée : la saisie est permise hors des dates.
  if (ouvertureAvances) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
        <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
        <div className="text-sm text-emerald-900">
          <p className="font-semibold">Ouverture exceptionnelle des avances</p>
          <p className="mt-0.5 leading-relaxed">
            Les avances restent saisissables jusqu'au <strong>{formaterDateLongue(ouvertureAvances.date_fin)}</strong>
            {ouvertureAvances.validateur_nom ? `, ouverture accordée par ${ouvertureAvances.validateur_nom}` : ''}.
            En temps normal, elles ne le sont que {periode}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
      <div className="text-sm text-blue-900">
        <p className="font-semibold">Calendrier de paie</p>
        <p className="mt-0.5 leading-relaxed">
          Les avances créées sont automatiquement déduites du salaire du mois sélectionné.
          {fenetreConfiguree && (
            <> Elles ne sont saisissables que <strong>{periode}</strong> : hors de ces dates, le système bloque leur création (une ouverture exceptionnelle peut être demandée depuis l'état des salaires).</>
          )} Une fois l'état des salaires validé par la comptabilité, le mois est figé : plus aucune
          avance ne peut y être imputée.
        </p>
      </div>
    </div>
  );
};

// Gabarits KLASYS des fenêtres (DESIGN-SYSTEM.md)
const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const ZONE_TEXTE = 'w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const LIBELLE = 'mb-1 block text-sm font-medium text-gray-700';
const BOUTON_PIED = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md px-4 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_SECONDAIRE = `${BOUTON_PIED} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
const BOUTON_PRINCIPAL = `${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a]`;
const BOUTON_DANGER = `${BOUTON_PIED} bg-[#c66d59] text-white hover:bg-[#ae5d4a]`;
const CONTENU_DIALOG = 'w-[95vw] !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0';
const ENTETE_DIALOG = 'flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left';
const PIED_DIALOG = 'gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0';

// Statut d'une avance : badge de largeur fixe (DESIGN-SYSTEM.md, « Badges »),
// pour que la colonne reste alignée d'une ligne à l'autre.
const STATUTS_AVANCE = {
  en_attente: { libelle: 'En attente', classe: 'bg-amber-50 text-amber-800 border-amber-200' },
  validee: { libelle: 'Validée', classe: 'bg-green-50 text-green-800 border-green-200' },
  appliquee: { libelle: 'Appliquée', classe: 'bg-blue-50 text-blue-800 border-blue-200' },
  annulee: { libelle: 'Annulée', classe: 'bg-gray-50 text-gray-600 border-gray-200' }
};

const BadgeStatutAvance = ({ statut }) => {
  const style = STATUTS_AVANCE[statut] || { libelle: statut, classe: 'bg-gray-50 text-gray-700 border-gray-200' };
  return (
    <span className={`inline-flex h-6 w-[96px] flex-shrink-0 items-center justify-center rounded-full border text-xs font-medium whitespace-nowrap ${style.classe}`}>
      {style.libelle}
    </span>
  );
};

const PastilleDialog = ({ icone: Icone, danger = false }) => (
  <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-white ${danger ? 'bg-[#c66d59]' : 'bg-[#246990]'}`}>
    <Icone size={18} />
  </div>
);

const AvancesSalaire = ({
  selectedDate,
  setSelectedDate,
  anneeActive,
  showToast,
  formatDateForDisplay,
  goToPreviousMonth,
  goToNextMonth,
  onBackToDashboard
}) => {
  // ============================================================================
  // 🎯 ÉTATS PRINCIPAUX
  // ============================================================================

  // Navigation et sélection
  const [typeEmploye, setTypeEmploye] = useState('FIXE'); // 'FIXE' | 'HORAIRE' | 'HYBRIDE'
  const [selectedEmploye, setSelectedEmploye] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Calendrier de paie : fenetre de saisie et gel du mois selectionne
  const [moisFige, setMoisFige] = useState(null);
  // Suivi du chargement de ce qui décide de la note et du blocage de saisie.
  const [moisChargesAvances, setMoisChargesAvances] = useState(false);
  const [parametresChargesAvances, setParametresChargesAvances] = useState(false);
  const [moisFigeLuPour, setMoisFigeLuPour] = useState(null);
  const [erreurCalendrierAvances, setErreurCalendrierAvances] = useState('');

  // Annulation privilégiée : porte de secours quand le gestionnaire qui a
  // accordé l'avance ne peut plus l'annuler (dérogation, Centre de Support).
  const { peutAnnuler: peutAnnulerPrivilegie, peutLireJournal } = usePermissionsAnnulationPrivilegiee();
  const [avancePrivilegiee, setAvancePrivilegiee] = useState(null);
  const [clePrivilegiee, setClePrivilegiee] = useState(0);
  const [journalOuvert, setJournalOuvert] = useState(false);

  // Données
  const [employes, setEmployes] = useState([]); // Personnel FIXE ou Enseignants HORAIRE
  const [moisScolaires, setMoisScolaires] = useState([]);
  const [loading, setLoading] = useState(false);

  // Avances
  const [historiqueAvances, setHistoriqueAvances] = useState([]);
  const [avancesMoisCourant, setAvancesMoisCourant] = useState([]);
  const [totalAvancesMois, setTotalAvancesMois] = useState(0);

  // 🆕 Statistiques
  const [statistiques, setStatistiques] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [parametresSalaires, setParametresSalaires] = useState({
    max_avances_salaire_mois: 1,
    pourcentage_max_avance_salaire: 50
  });

  // 🆕 Accordéons pour catégories (FERMÉS PAR DÉFAUT)
  const [categorieOuverte, setCategorieOuverte] = useState({
    fixe: false,
    horaire: false,
    hybride: false
  });

  // 🆕 Accordéon pour stats (FERMÉ PAR DÉFAUT)
  const [statsOuvertes, setStatsOuvertes] = useState(false);

  // Modal création
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [montantAvance, setMontantAvance] = useState('');
  const [motifAvance, setMotifAvance] = useState('');
  const [isCreatingAvance, setIsCreatingAvance] = useState(false);

  // Modal modification
  const [showEditModal, setShowEditModal] = useState(false);
  const [avanceToEdit, setAvanceToEdit] = useState(null);
  const [montantEdit, setMontantEdit] = useState('');
  const [motifEdit, setMotifEdit] = useState('');
  const [justificationEdit, setJustificationEdit] = useState('');
  const [isEditingAvance, setIsEditingAvance] = useState(false);

  // Modal suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [avanceToDelete, setAvanceToDelete] = useState(null);
  const [justificationDelete, setJustificationDelete] = useState('');
  const [isDeletingAvance, setIsDeletingAvance] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const employesParPage = 10;

  // L'annee vient du tableau de bord parent, avec repli sur le stockage local
  // tant que le parent n'a pas fini de la resoudre.
  const anneeScolaireId = anneeActive?.id || localStorage.getItem('annee_scolaire_id') || '';

  // Fenetre des avances, exprimee en jours RELATIFS au mois de paie : jour 1 =
  // date_debut du mois configure. Elle ne s'applique qu'au mois de paie courant ;
  // sur un mois passe, ce sont les verrous d'etat qui tranchent cote serveur.
  // Bornes non configurees = aucune contrainte. Miroir de evaluerFenetre().
  const avanceDebut = Number(parametresSalaires?.avance_jour_debut || 0);
  const avanceFin = Number(parametresSalaires?.avance_jour_fin || 0);

  const moisAvance = moisScolaires.find((m) => String(m.id) === String(selectedMonth));
  // Fenêtre réelle du mois : week-ends reportés au jour ouvré suivant, comme
  // le serveur (utils/fenetresPaie.js).
  const fenetreAvance = moisAvance?.date_debut && moisAvance?.date_fin
    ? fenetreEffective({
      dateDebutMois: String(moisAvance.date_debut).slice(0, 10),
      dateFinMois: String(moisAvance.date_fin).slice(0, 10),
      debut: avanceDebut,
      fin: avanceFin
    })
    : null;
  const periodeAvances = fenetreAvance
    ? (fenetreAvance.date_debut === fenetreAvance.date_fin
      ? `le ${formaterDateLongue(fenetreAvance.date_debut)}`
      : `du ${formaterDateLongue(fenetreAvance.date_debut)} au ${formaterDateLongue(fenetreAvance.date_fin)}`)
    : null;

  // Ouverture exceptionnelle de l'étape « Avances » pour ce mois (serveur).
  const [ouvertureAvances, setOuvertureAvances] = useState(null);
  // Mois pour lequel l'ouverture a été lue : tant qu'il diffère du mois choisi,
  // la note reste en « Lecture… ».
  const [ouvertureLuePour, setOuvertureLuePour] = useState(null);
  useEffect(() => {
    if (!selectedMonth) { setOuvertureAvances(null); setOuvertureLuePour(null); return undefined; }
    let annule = false;
    fetch(`/api/etat-salaires/ouvertures/actives?mois_scolaire_id=${encodeURIComponent(selectedMonth)}`, { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => { if (!annule) setOuvertureAvances(data?.data?.avances || null); })
      .catch(() => { if (!annule) setOuvertureAvances(null); })
      .finally(() => { if (!annule) setOuvertureLuePour(selectedMonth); });
    return () => { annule = true; };
  }, [selectedMonth]);

  const fenetreAvanceOuverte = (() => {
    if (!avanceDebut || !avanceFin) return true;
    if (ouvertureAvances) return true;

    const moisSelectionne = moisAvance;
    if (!moisSelectionne?.date_debut || !moisSelectionne?.date_fin) return true;

    const aujourdHui = new Date();
    const jour = [
      aujourdHui.getFullYear(),
      String(aujourdHui.getMonth() + 1).padStart(2, '0'),
      String(aujourdHui.getDate()).padStart(2, '0')
    ].join('-');
    const debut = String(moisSelectionne.date_debut).slice(0, 10);
    const fin = String(moisSelectionne.date_fin).slice(0, 10);

    // Mois passe ou a venir : la fenetre ne s'applique pas.
    if (jour < debut || jour > fin) return true;

    const enJours = (valeur) => Math.floor(new Date(`${valeur}T00:00:00`).getTime() / 86400000);
    const jourRang = enJours(jour) - enJours(debut) + 1;
    const nbJours = enJours(fin) - enJours(debut) + 1;
    const debutEffectif = fenetreAvance ? fenetreAvance.rang_debut : avanceDebut;
    const finEffective = fenetreAvance ? fenetreAvance.rang_fin : Math.min(avanceFin, nbJours);

    return avanceDebut <= avanceFin
      ? jourRang >= debutEffectif && jourRang <= finEffective
      : jourRang >= debutEffectif || jourRang <= finEffective;
  })();

  // Aucun mois de paie choisi (la date de l'en-tête n'appartient à aucun mois) :
  // rien n'est rattachable tant qu'un mois n'est pas choisi dans la grille.
  const horsCalendrier = moisScolaires.length > 0 && !selectedMonth;

  // La note et le blocage attendent que tout soit lu pour le mois affiché.
  const cleMoisFige = moisFigeCle(moisAvance, selectedDate, anneeScolaireId);
  const calendrierEnLecture = !moisChargesAvances
    || !parametresChargesAvances
    || moisFigeLuPour !== cleMoisFige
    || (Boolean(selectedMonth) && ouvertureLuePour !== selectedMonth);

  const saisieAvanceBloquee = calendrierEnLecture || horsCalendrier || Boolean(moisFige) || !fenetreAvanceOuverte;
  const motifBlocageAvance = calendrierEnLecture
    ? 'Lecture du calendrier de paie en cours…'
    : horsCalendrier
    ? 'Choisissez le mois concerné dans la grille.'
    : moisFige
      ? "L'état des salaires de ce mois est validé : le mois est figé, plus aucune avance ne peut y être imputée."
      : `Les avances ne sont autorisées que du jour ${avanceDebut} au jour ${avanceFin} du mois de paie (calendrier de paie).`;

  // ============================================================================
  // 🎨 FONCTIONS UTILITAIRES
  // ============================================================================

  const getTypeEmployeConfig = (type = typeEmploye) => {
    if (type === 'HORAIRE') {
      return {
        title: 'SALAIRE TAUX HORAIRE',
        description: 'Enseignants a taux horaire',
        referenceLabel: 'Salaire theorique',
        roleLabel: 'Enseignant a taux horaire',
        emptyLabel: 'Aucun enseignant trouve pour ce mois',
        Icon: GraduationCap
      };
    }

    if (type === 'HYBRIDE') {
      return {
        title: 'SALAIRES HYBRIDES',
        description: 'Employes avec remuneration fixe et horaire',
        referenceLabel: 'Salaire hybride de reference',
        roleLabel: 'Employe hybride',
        emptyLabel: 'Aucun employe hybride trouve pour ce mois',
        Icon: Briefcase
      };
    }

    return {
      title: 'SALAIRE FIXE',
      description: 'Personnel administratif et enseignants fixes',
      referenceLabel: 'Salaire de base',
      roleLabel: 'Employe',
      emptyLabel: 'Aucun employe a salaire fixe trouve',
      Icon: Building
    };
  };

  const formatAnneeScolaire = (anneeScolaire) => {
    if (!anneeScolaire) {
      return <span className="text-red-600 text-xs">⚠️ Année non définie</span>;
    }
    return anneeScolaire;
  };

  // Mois de paie contenant la date, selon le calendrier configure de l'ecole.
  // On compare les bornes date_debut / date_fin, jamais le nom du mois : une
  // ecole peut nommer ses mois comme elle veut et les decaler (paie du 5 au 4).
  // Les dates sont comparees en YYYY-MM-DD pour eviter tout decalage de fuseau.
  const findMoisScolaireByDate = (date) => {
    const jour = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');

    return moisScolaires.find((mois) => {
      if (!mois.date_debut || !mois.date_fin) return false;
      const debut = String(mois.date_debut).slice(0, 10);
      const fin = String(mois.date_fin).slice(0, 10);
      return jour >= debut && jour <= fin;
    });
  };

  // ============================================================================
  // 🔄 EFFETS (useEffect)
  // ============================================================================

  // Charger les données initiales
  useEffect(() => {
    loadMoisScolaires();
    loadParametresSalaires();
  }, []);

  // Mois selectionne = mois de paie contenant la date affichee. Si la date tombe
  // hors du calendrier configure (vacances d'une ecole qui declare 9 mois), on ne
  // selectionne RIEN : l'ancien repli sur moisScolaires[0] placait silencieusement
  // le gestionnaire sur le premier mois de l'annee, souvent deja paye.
  useEffect(() => {
    if (moisScolaires.length === 0) return;
    const moisTrouve = findMoisScolaireByDate(selectedDate);
    setSelectedMonth(moisTrouve ? moisTrouve.id.toString() : '');
  }, [selectedDate, moisScolaires]);

  // Une seule sélection de mois : cliquer un mois de la grille déplace aussi la
  // date commune (l'en-tête du module de paie), qui resélectionne ce mois. Sans
  // cela, l'en-tête restait sur septembre quand on choisissait juin.
  const choisirMois = (mois) => {
    setSelectedMonth(mois.id.toString());
    const debut = String(mois.date_debut || '').slice(0, 10);
    if (setSelectedDate && /^\d{4}-\d{2}-\d{2}$/.test(debut)) {
      const [annee, numeroMois, jour] = debut.split('-').map(Number);
      setSelectedDate(new Date(annee, numeroMois - 1, jour, 12, 0, 0));
    }
  };

  // Le personnel fixe ne dépend pas du mois : chargé une fois par passage sur
  // l'onglet. Il se rechargeait à chaque changement de mois, et le mois
  // changeait trois fois à l'ouverture (vide, premier mois, bon mois).
  useEffect(() => {
    if (typeEmploye === 'FIXE') loadEmployesFixes();
  }, [typeEmploye]);

  // Taux horaire et hybrides : leur liste dépend du mois, elle attend qu'il
  // soit connu.
  useEffect(() => {
    if (typeEmploye === 'FIXE') return;
    if (typeEmploye === 'HORAIRE') loadEnseignantsHoraire();
    else loadEmployesHybrides();
  }, [typeEmploye, selectedMonth]);

  // Charger les avances et stats quand employé sélectionné
  useEffect(() => {
    if (selectedEmploye) {
      loadHistoriqueAvances(selectedEmploye);
      if (selectedMonth) {
        loadAvancesMois(selectedEmploye, selectedMonth);
      }
    }
  }, [selectedEmploye, selectedMonth]);

  // Charger les statistiques quand le mois change
  useEffect(() => {
    if (selectedMonth) {
      loadStatistiques(selectedMonth);
    }
  }, [selectedMonth]);

  // Etat de gel du mois affiche : le serveur reste seul juge, on reflete ici
  // l'information pour desactiver la saisie et expliquer pourquoi.
  // Lu pour le MOIS CHOISI (son mois civil de début), plus pour la date de
  // l'en-tête : cliquer sur juin en septembre lisait l'état de septembre.
  useEffect(() => {
    let annule = false;
    const cle = moisFigeCle(moisAvance, selectedDate, anneeScolaireId);

    const chargerMoisFige = async () => {
      if (!anneeScolaireId) {
        setMoisFige(null);
        setMoisFigeLuPour(cle);
        return;
      }
      try {
        const response = await api.etatSalaires.getMoisFiges(anneeScolaireId);
        if (annule) return;
        if (!response.success) throw new Error(response.message || 'Lecture impossible');

        const [annee, mois] = cle.split('|').slice(1).map(Number);
        setMoisFige(
          (response.data || []).find(
            (ligne) => Number(ligne.mois) === mois && Number(ligne.annee) === annee
          ) || null
        );
        setErreurCalendrierAvances('');
      } catch (error) {
        console.error('Erreur lecture mois figes:', error);
        if (!annule) setErreurCalendrierAvances("Le calendrier de paie n'a pas pu être lu. Actualisez la page.");
      } finally {
        if (!annule) setMoisFigeLuPour(cle);
      }
    };

    chargerMoisFige();
    return () => { annule = true; };
  }, [anneeScolaireId, selectedDate, moisAvance?.id]);

  // ============================================================================
  // 📡 FONCTIONS DE CHARGEMENT DES DONNÉES
  // ============================================================================

  const loadParametresSalaires = async () => {
    try {
      const response = await api.parametresSalaires.getByAnnee(anneeScolaireId);

      if (response.success && response.data) {
        setParametresSalaires({
          max_avances_salaire_mois: Number(response.data.max_avances_salaire_mois || 1),
          pourcentage_max_avance_salaire: Number(response.data.pourcentage_max_avance_salaire || 50),
          // Bornes du calendrier de paie, appliquees cote serveur a la creation
          avance_jour_debut: Number(response.data.avance_jour_debut || 0),
          avance_jour_fin: Number(response.data.avance_jour_fin || 0)
        });
      }
    } catch (error) {
      console.error('Erreur chargement parametres salaires:', error);
      setErreurCalendrierAvances("Le calendrier de paie n'a pas pu être lu. Actualisez la page.");
    } finally {
      setParametresChargesAvances(true);
    }
  };

  // Charger les mois scolaires
  const loadMoisScolaires = async () => {
    try {
      const response = await api.moisScolaires.getAll();

      if (response.success && response.data) {
        if (response.data.length === 0) {
          showToast('Aucun mois scolaire configuré', 'info');
          return;
        }

        // Le mois est choisi par l'effet qui suit `moisScolaires`, et lui seul.
        // Le chercher ici lisait une liste encore vide : la recherche échouait
        // et le premier mois de l'année était sélectionné un instant.
        setMoisScolaires(response.data);
      } else {
        showToast(response.message || 'Erreur lors du chargement des mois', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement mois:', error);
      showToast('Erreur lors du chargement des mois scolaires', 'error');
      setErreurCalendrierAvances("Le calendrier de paie n'a pas pu être lu. Actualisez la page.");
    } finally {
      setMoisChargesAvances(true);
    }
  };

  // Chaque chargement de liste reçoit un numéro : seule la réponse du dernier
  // est appliquée. Sinon une réponse lente, arrivée après un changement
  // d'onglet ou de mois, remplaçait la liste affichée.
  const requeteEmployesRef = useRef(0);
  const estDerniereRequete = (numero) => numero === requeteEmployesRef.current;

  // Charger le personnel FIXE
  const loadEmployesFixes = async () => {
    const numero = ++requeteEmployesRef.current;
    setLoading(true);
    try {
      const response = await api.salary.getEmployees();
      if (!estDerniereRequete(numero)) return;

      if (response.success) {
        const employesFixes = response.data.filter(e => {
          const isHybrid = Number(e.est_hybride || 0) === 1 || e.profil_remuneration === 'HYBRIDE';
          return !isHybrid && (
            e.type_contrat === 'FIXE'
            || e.mode_remuneration === 'MENSUEL'
            || e.mode_remuneration === 'FIXE'
          );
        });

        setEmployes(employesFixes);

        if (employesFixes.length === 0) {
          showToast('Aucun employé à salaire fixe trouvé', 'info');
        }
      } else {
        showToast(response.message || 'Erreur lors du chargement', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement employés fixes:', error);
      if (estDerniereRequete(numero)) showToast('Erreur lors du chargement des employés', 'error');
    } finally {
      if (estDerniereRequete(numero)) setLoading(false);
    }
  };

  // 🆕 Charger les enseignants TAUX HORAIRE
  const loadEnseignantsHoraire = async () => {
    const numero = ++requeteEmployesRef.current;
    if (!selectedMonth) {
      setEmployes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.avancesSalaire.getEnseignantsTauxHoraire(selectedMonth);
      if (!estDerniereRequete(numero)) return;

      if (response.success) {
        setEmployes(response.data);

        if (response.data.length === 0) {
          showToast('Aucun enseignant trouvé pour ce mois', 'info');
        }
      } else {
        showToast(response.message || 'Erreur lors du chargement', 'error');
        setEmployes([]);
      }
    } catch (error) {
      console.error('Erreur chargement enseignants horaire:', error);
      if (estDerniereRequete(numero)) {
        showToast('Erreur lors du chargement des enseignants', 'error');
        setEmployes([]);
      }
    } finally {
      if (estDerniereRequete(numero)) setLoading(false);
    }
  };

  // 🆕 Charger les statistiques
  const loadEmployesHybrides = async () => {
    const numero = ++requeteEmployesRef.current;
    if (!selectedMonth) {
      setEmployes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.avancesSalaire.getEmployesHybrides(selectedMonth);
      if (!estDerniereRequete(numero)) return;

      if (response.success) {
        setEmployes(response.data || []);

        if (!response.data || response.data.length === 0) {
          showToast(getTypeEmployeConfig('HYBRIDE').emptyLabel, 'info');
        }
      } else {
        showToast(response.message || 'Erreur lors du chargement des employes hybrides', 'error');
        setEmployes([]);
      }
    } catch (error) {
      console.error('Erreur chargement employes hybrides:', error);
      if (estDerniereRequete(numero)) {
        showToast('Erreur lors du chargement des employés hybrides', 'error');
        setEmployes([]);
      }
    } finally {
      if (estDerniereRequete(numero)) setLoading(false);
    }
  };

  const loadStatistiques = async (moisId) => {
    setLoadingStats(true);
    try {
      const response = await api.avancesSalaire.getStatistiques(moisId);

      if (response.success) {
        setStatistiques(response.data);
      } else {
        setStatistiques(null);
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      setStatistiques(null);
    } finally {
      setLoadingStats(false);
    }
  };

  // Charger l'historique des avances
  const loadHistoriqueAvances = async (employeId) => {
    try {
      const response = await api.avancesSalaire.getHistoriqueAvances(employeId);

      if (response.success) {
        setHistoriqueAvances(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement historique avances:', error);
    }
  };

  // Charger les avances du mois
  const loadAvancesMois = async (employeId, monthId) => {
    try {
      const response = await api.avancesSalaire.getAvancesMois(employeId, monthId);

      if (response.success) {
        setAvancesMoisCourant(response.data.avances);
        setTotalAvancesMois(response.data.total_avances);
      }
    } catch (error) {
      console.error('Erreur chargement avances du mois:', error);
    }
  };

  // ============================================================================
  // 🎬 FONCTIONS DE GESTION DES AVANCES
  // ============================================================================

  // Obtenir l'employé sélectionné
  const getSelectedEmploye = () => {
    return employes.find(e => e.id.toString() === selectedEmploye);
  };

  // Obtenir le mois sélectionné
  const getSelectedMonth = () => {
    return moisScolaires.find(m => m.id.toString() === selectedMonth);
  };

  // Obtenir le salaire de référence selon le type
  const getSalaireReference = () => {
    const employe = getSelectedEmploye();
    if (!employe) return 0;

    if (typeEmploye === 'FIXE') {
      return parseFloat(employe.salaire || 0);
    }

    if (typeEmploye === 'HYBRIDE') {
      return parseFloat(employe.salaire_reference || employe.salaire_theorique || employe.salaire || 0);
    }

    return parseFloat(employe.salaire_theorique || 0);
  };

  const getPlafondAvance = () => {
    const pourcentage = Number(parametresSalaires.pourcentage_max_avance_salaire || 50);
    return Math.floor(getSalaireReference() * pourcentage / 100);
  };

  const getNombreAvancesMois = () => {
    return avancesMoisCourant.filter((avance) => avance.statut !== 'annulee').length;
  };

  const getDisponibleAvance = () => {
    return Math.max(0, getPlafondAvance() - Number(totalAvancesMois || 0));
  };

  const isLimiteNombreAvancesAtteinte = () => {
    return getNombreAvancesMois() >= Number(parametresSalaires.max_avances_salaire_mois || 1);
  };

  const formatMontant = (value) => {
    return Number(value || 0).toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const getEmployeSecondaryLabel = (employe) => {
    if (!employe) return '';
    if (typeEmploye === 'FIXE') return employe.role_principal || 'Employe';
    if (typeEmploye === 'HYBRIDE') {
      const composantesCount = Number(employe.composantes_count || employe.composantes?.length || 0);
      return composantesCount + ' composante' + (composantesCount > 1 ? 's' : '') + ' - ' + formatMontant(employe.salaire_reference || employe.salaire_theorique || employe.salaire) + ' FCFA';
    }
    return (employe.total_heures_mois || 0) + 'h/mois';
  };

  const getSelectedEmployeRoleLabel = () => {
    const employe = getSelectedEmploye();
    if (!employe) return '';
    if (typeEmploye === 'FIXE') return employe.role_principal || 'Employe';
    return getTypeEmployeConfig().roleLabel;
  };

// Ouvrir le modal de création
  const handleOpenCreateModal = () => {
    // Le serveur refuse de toute facon : on evite au gestionnaire une saisie inutile.
    if (saisieAvanceBloquee) {
      showToast(motifBlocageAvance, 'error');
      return;
    }

    if (isLimiteNombreAvancesAtteinte()) {
      showToast(`Limite atteinte : ${parametresSalaires.max_avances_salaire_mois} avance(s) maximum pour ce mois`, 'info');
      return;
    }

    if (getDisponibleAvance() <= 0) {
      showToast("Plafond d'avance déjà atteint pour ce mois", 'info');
      return;
    }

    setMontantAvance('');
    setMotifAvance('');
    setShowCreateModal(true);
  };

  // Créer une nouvelle avance
  const handleCreateAvance = async () => {
    const montantDemande = parseFloat(montantAvance);

    if (!montantAvance || montantDemande <= 0) {
      showToast('Veuillez saisir un montant valide', 'error');
      return;
    }

    if (!selectedMonth) {
      showToast('Veuillez sélectionner un mois', 'error');
      return;
    }

    if (isLimiteNombreAvancesAtteinte()) {
      showToast(`Limite atteinte : ${parametresSalaires.max_avances_salaire_mois} avance(s) maximum pour ce mois`, 'info');
      return;
    }

    if (montantDemande > getDisponibleAvance()) {
      showToast(`Montant refuse : maximum disponible ${formatMontant(getDisponibleAvance())} FCFA`, 'error');
      return;
    }

    setIsCreatingAvance(true);

    try {
      const response = await api.avancesSalaire.creerAvance({
        employe_id: parseInt(selectedEmploye),
        mois_scolaire_id: parseInt(selectedMonth),
        montant: montantDemande,
        motif: motifAvance || null,
        type_employe: typeEmploye
      });

      if (response.success) {
        showToast('Avance créée avec succès', 'success');
        setShowCreateModal(false);

        // Recharger les données
        loadHistoriqueAvances(selectedEmploye);
        loadAvancesMois(selectedEmploye, selectedMonth);
        loadStatistiques(selectedMonth); // ✅ Recharger les stats

        // Réinitialiser le formulaire
        setMontantAvance('');
        setMotifAvance('');
      } else {
        showToast(response.message || 'Erreur lors de la création', 'error');
      }
    } catch (error) {
      console.error('Erreur création avance:', error);
      showToast('Erreur lors de la création de l\'avance', 'error');
    } finally {
      setIsCreatingAvance(false);
    }
  };

  // Ouvrir le modal de modification
  const handleOpenEditModal = (avance) => {
    setAvanceToEdit(avance);
    setMontantEdit(avance.montant.toString());
    setMotifEdit(avance.motif || '');
    setJustificationEdit('');
    setShowEditModal(true);
  };

  // Modifier une avance
  const handleEditAvance = async () => {
    if (!justificationEdit.trim()) {
      showToast('Veuillez justifier la modification', 'error');
      return;
    }

    if (!montantEdit || parseFloat(montantEdit) <= 0) {
      showToast('Veuillez saisir un montant valide', 'error');
      return;
    }

    setIsEditingAvance(true);

    try {
      const response = await api.avancesSalaire.modifierAvance(avanceToEdit.id, {
        montant: parseFloat(montantEdit),
        motif: motifEdit,
        justification_modification: justificationEdit
      });

      if (response.success) {
        showToast('Avance modifiée avec succès', 'success');
        setShowEditModal(false);

        // Recharger les données
        loadHistoriqueAvances(selectedEmploye);
        loadAvancesMois(selectedEmploye, selectedMonth);
        loadStatistiques(selectedMonth); // ✅ Recharger les stats
      } else {
        showToast(response.message || 'Erreur lors de la modification', 'error');
      }
    } catch (error) {
      console.error('Erreur modification avance:', error);
      showToast('Erreur lors de la modification', 'error');
    } finally {
      setIsEditingAvance(false);
    }
  };

  // Ouvrir le modal de suppression
  const handleOpenDeleteModal = (avance) => {
    setAvanceToDelete(avance);
    setJustificationDelete('');
    setShowDeleteModal(true);
  };

  // Supprimer une avance
  const handleDeleteAvance = async () => {
    if (!justificationDelete.trim()) {
      showToast('Veuillez justifier la suppression', 'error');
      return;
    }

    setIsDeletingAvance(true);

    try {
      const response = await api.avancesSalaire.supprimerAvance(
        avanceToDelete.id,
        justificationDelete
      );

      if (response.success) {
        showToast('Avance annulée avec succès', 'success');
        setShowDeleteModal(false);

        // Recharger les données
        loadHistoriqueAvances(selectedEmploye);
        loadAvancesMois(selectedEmploye, selectedMonth);
        loadStatistiques(selectedMonth); // ✅ Recharger les stats
      } else {
        showToast(response.message || 'Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      console.error('Erreur suppression avance:', error);
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setIsDeletingAvance(false);
    }
  };

  // ============================================================================
  // 📊 CALCULS ET HELPERS
  // ============================================================================

  // Grouper les mois par trimestre
  const moisParTrimestre = moisScolaires.reduce((acc, mois) => {
    const trimestre = mois.trimestre || 1;
    if (!acc[trimestre]) acc[trimestre] = [];
    acc[trimestre].push(mois);
    return acc;
  }, {});

  // Pagination
  const employesFiltres = employes.filter(e =>
    `${e.prenom} ${e.nom}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexDernierEmploye = currentPage * employesParPage;
  const indexPremierEmploye = indexDernierEmploye - employesParPage;
  const employesAffiches = employesFiltres.slice(indexPremierEmploye, indexDernierEmploye);
  const nombrePages = Math.ceil(employesFiltres.length / employesParPage);
  const typeConfig = getTypeEmployeConfig();
  const TypeEmployeIcon = typeConfig.Icon;

  // ============================================================================
  // 🎨 RENDU DU COMPOSANT
  // ============================================================================

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">

        {/* ========== EN-TETE DE PAGE ========== */}
        <div className="bg-[#246990] px-6 py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onBackToDashboard}
                className="rounded-lg bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
                title="Retour"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="rounded-full bg-white/15 p-2.5">
                <HandCoins className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Avance sur salaire</h2>
                <p className="text-sm text-white/80">Gestion des avances pour tout le personnel</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
            {peutLireJournal && (
              <button
                type="button"
                onClick={() => setJournalOuvert(true)}
                className="inline-flex h-9 items-center gap-2 rounded-md bg-white/10 px-3 text-sm font-medium text-white transition-colors hover:bg-white/20"
              >
                <ScrollText className="h-4 w-4" />
                Journal des actions privilégiées
              </button>
            )}
            {/* Navigation par mois, pilotee par le tableau de bord parent */}
            <div className="flex items-center gap-1 rounded-md bg-white/10 p-1">
              <button
                onClick={goToPreviousMonth}
                className="rounded p-2 text-white transition-colors hover:bg-white/20"
                title="Mois précédent"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3 text-sm font-medium capitalize text-white">
                {formatDateForDisplay(selectedDate)}
              </span>
              <button
                onClick={goToNextMonth}
                className="rounded p-2 text-white transition-colors hover:bg-white/20"
                title="Mois suivant"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* ========== NOTE CALENDRIER DE PAIE ========== */}
        <div className="px-4 pt-4 md:px-6">
          <NoteCalendrierPaie
            parametres={parametresSalaires}
            moisFige={moisFige}
            fenetreOuverte={fenetreAvanceOuverte}
            horsCalendrier={horsCalendrier}
            periodeAvances={periodeAvances}
            ouvertureAvances={ouvertureAvances}
            enLecture={calendrierEnLecture}
            erreurLecture={erreurCalendrierAvances}
            libelleDateAffichee={formatDateForDisplay(selectedDate)}
          />
        </div>

        {/* ========== CONTENU PRINCIPAL ========== */}
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-full">

            {/* ========== COLONNE 1 : STATISTIQUES & LISTE ========== */}
            <div className="lg:col-span-3 space-y-4">

              {/* 🆕 BLOC STATISTIQUES GLOBALES AVEC ACCORDÉON */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <button
                  onClick={() => setStatsOuvertes(!statsOuvertes)}
                  className="w-full p-3 bg-gradient-to-r from-[#246990] to-[#1f5a7a] rounded-lg mb-4 flex items-center justify-between hover:from-[#1f5a7a] hover:to-[#246990] transition-all"
                >
                  <div className="flex items-center text-white">
                    <PieChart className="w-4 h-4 mr-2" />
                    <h3 className="font-medium text-sm">Statistiques du mois</h3>
                  </div>
                  {statsOuvertes ?
                    <ChevronDown className="w-4 h-4 text-white" /> :
                    <ChevronRight className="w-4 h-4 text-white" />
                  }
                </button>

                <AnimatePresence>
                  {statsOuvertes && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      {loadingStats ? (
                        <div className="text-center py-4">
                          <div className="animate-spin w-6 h-6 border-4 border-[#246990] border-t-transparent rounded-full mx-auto"></div>
                        </div>
                      ) : !statistiques ? (
                        <div className="text-center py-4 text-gray-400 text-xs">
                          Sélectionnez un mois
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Mois sélectionné */}
                          <div className="text-center pb-3 border-b border-gray-200">
                            <div className="text-xs text-gray-500">Période</div>
                            <div className="font-bold text-[#246990]">
                              {statistiques.mois?.nom} {statistiques.mois?.annee_scolaire}
                            </div>
                          </div>

                          {/* Total général */}
                          <div
                            className="p-3 rounded-lg border"
                            style={{
                              background: "linear-gradient(to bottom right, #e6f4f3, #e0f1ef)",
                              borderColor: "#319190"
                            }}
                          >
                            <div className="text-center">
                              <div className="text-xs mb-1" style={{ color: "#319190" }}>
                                Total des avances
                              </div>

                              <div className="text-xl font-bold" style={{ color: "#319190" }}>
                                {statistiques.total_general.montant_total.toLocaleString()} FCFA
                              </div>

                              <div className="text-xs mt-1" style={{ color: "#319190" }}>
                                {statistiques.total_general.nombre_personnes} personne(s) • {statistiques.total_general.nombre_avances} avance(s)
                              </div>
                            </div>
                          </div>


                          {/* 🆕 ACCORDÉON PERSONNEL FIXE */}
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => setCategorieOuverte(prev => ({ ...prev, fixe: !prev.fixe }))}
                              className="w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <Building className="w-4 h-4 text-[#246990]" />
                                Personnel fixe
                              </div>
                              <div className="flex items-center gap-3">
                                <BulleAvancesCategorie
                                  titre="Personnel fixe"
                                  personnes={statistiques.personnel_fixe?.personnes}
                                  messageVide="Aucune avance pour le personnel fixe sur ce mois."
                                />
                                {categorieOuverte.fixe ?
                                  <ChevronDown className="w-4 h-4 text-gray-500" /> :
                                  <ChevronRight className="w-4 h-4 text-gray-500" />
                                }
                              </div>
                            </button>

                            <AnimatePresence>
                              {categorieOuverte.fixe && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="p-3 space-y-2 text-xs bg-white">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Employés :</span>
                                      <span className="font-semibold text-gray-900">
                                        {statistiques.personnel_fixe.nombre_employes}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Avances :</span>
                                      <span className="font-semibold text-gray-900">
                                        {statistiques.personnel_fixe.nombre_avances}
                                      </span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-gray-200">
                                      <span className="text-gray-600">Montant total :</span>
                                      <span className="font-bold text-[#246990]">
                                        {statistiques.personnel_fixe.montant_total.toLocaleString()} FCFA
                                      </span>
                                    </div>
                                    {statistiques.personnel_fixe.nombre_avances > 0 && (
                                      <div className="flex justify-between text-gray-500">
                                        <span>Moyenne :</span>
                                        <span>
                                          {Math.round(statistiques.personnel_fixe.montant_moyen).toLocaleString()} FCFA
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                          {/* 🆕 ACCORDÉON ENSEIGNANTS HORAIRE */}
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => setCategorieOuverte(prev => ({ ...prev, horaire: !prev.horaire }))}
                              className="w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <GraduationCap className="w-4 h-4 text-[#246990]" />
                                Enseignants à taux horaire
                              </div>
                              <div className="flex items-center gap-3">
                                <BulleAvancesCategorie
                                  titre="Enseignants à taux horaire"
                                  personnes={statistiques.enseignants_horaire?.personnes}
                                  messageVide="Aucune avance pour les enseignants à taux horaire sur ce mois."
                                />
                                {categorieOuverte.horaire ?
                                  <ChevronDown className="w-4 h-4 text-gray-500" /> :
                                  <ChevronRight className="w-4 h-4 text-gray-500" />
                                }
                              </div>
                            </button>

                            <AnimatePresence>
                              {categorieOuverte.horaire && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="p-3 space-y-2 text-xs bg-white">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Enseignants :</span>
                                      <span className="font-semibold text-gray-900">
                                        {statistiques.enseignants_horaire.nombre_enseignants}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Avances :</span>
                                      <span className="font-semibold text-gray-900">
                                        {statistiques.enseignants_horaire.nombre_avances}
                                      </span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-gray-200">
                                      <span className="text-gray-600">Montant total :</span>
                                      <span className="font-bold text-[#246990]">
                                        {statistiques.enseignants_horaire.montant_total.toLocaleString()} FCFA
                                      </span>
                                    </div>
                                    {statistiques.enseignants_horaire.nombre_avances > 0 && (
                                      <div className="flex justify-between text-gray-500">
                                        <span>Moyenne :</span>
                                        <span>
                                          {Math.round(statistiques.enseignants_horaire.montant_moyen).toLocaleString()} FCFA
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => setCategorieOuverte(prev => ({ ...prev, hybride: !prev.hybride }))}
                              className="w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <Briefcase className="w-4 h-4 text-[#246990]" />
                                Employés hybrides
                              </div>
                              <div className="flex items-center gap-3">
                                <BulleAvancesCategorie
                                  titre="Employés hybrides"
                                  personnes={statistiques.employes_hybrides?.personnes}
                                  messageVide="Aucune avance pour les employés hybrides sur ce mois."
                                />
                                {categorieOuverte.hybride ?
                                  <ChevronDown className="w-4 h-4 text-gray-500" /> :
                                  <ChevronRight className="w-4 h-4 text-gray-500" />
                                }
                              </div>
                            </button>

                            <AnimatePresence>
                              {categorieOuverte.hybride && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="p-3 space-y-2 text-xs bg-white">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Employes :</span>
                                      <span className="font-semibold text-gray-900">
                                        {statistiques.employes_hybrides?.nombre_employes || 0}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Avances :</span>
                                      <span className="font-semibold text-gray-900">
                                        {statistiques.employes_hybrides?.nombre_avances || 0}
                                      </span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-gray-200">
                                      <span className="text-gray-600">Montant total :</span>
                                      <span className="font-bold text-[#246990]">
                                        {(statistiques.employes_hybrides?.montant_total || 0).toLocaleString()} FCFA
                                      </span>
                                    </div>
                                    {(statistiques.employes_hybrides?.nombre_avances || 0) > 0 && (
                                      <div className="flex justify-between text-gray-500">
                                        <span>Moyenne :</span>
                                        <span>
                                          {Math.round(statistiques.employes_hybrides?.montant_moyen || 0).toLocaleString()} FCFA
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* LISTE DES EMPLOYÉS */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <div className="p-3 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-4">
                  <div className="p-2 bg-[#246990] rounded-full mr-2">
                    <TypeEmployeIcon className="text-white w-3 h-3 md:w-4 md:h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#246990] text-sm md:text-base truncate">
                      {typeConfig.title}
                    </h3>
                    <p className="text-xs text-gray-600 truncate">
                      {typeConfig.description}
                    </p>
                  </div>
                </div>

                {/* Toggle Type d'employé */}
                <div className="flex bg-gray-100 rounded-lg p-1 mb-3">
                  <button
                    onClick={() => {
                      setTypeEmploye('FIXE');
                      setSelectedEmploye('');
                      setCurrentPage(1);
                    }}
                    className={`flex-1 px-2 md:px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${typeEmploye === 'FIXE'
                        ? 'bg-white text-[#246990] shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >

                    <span className="hidden sm:inline"> SALAIRE FIXE</span>
                  </button>
                  <button
                    onClick={() => {
                      setTypeEmploye('HORAIRE');
                      setSelectedEmploye('');
                      setCurrentPage(1);
                    }}
                    className={`flex-1 px-2 md:px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${typeEmploye === 'HORAIRE'
                        ? 'bg-white text-[#246990] shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >

                    <span className="hidden sm:inline"> SALAIRE TAUX HORAIRE</span>
                  </button>
                  <button
                    onClick={() => {
                      setTypeEmploye('HYBRIDE');
                      setSelectedEmploye('');
                      setCurrentPage(1);
                    }}
                    className={'flex-1 px-2 md:px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ' + (
                      typeEmploye === 'HYBRIDE'
                        ? 'bg-white text-[#246990] shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    <span className="hidden sm:inline"> HYBRIDE</span>
                  </button>
                </div>

                {/* Barre de recherche */}
                <div className="mb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 md:w-4 md:h-4" />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full pl-8 md:pl-9 pr-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Liste */}
                <div className="space-y-2 max-h-[400px] md:max-h-[500px] overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8 text-gray-400">
                      <div className="animate-spin w-8 h-8 border-4 border-[#246990] border-t-transparent rounded-full mx-auto"></div>
                      <p className="text-sm mt-2">Chargement...</p>
                    </div>
                  ) : employesAffiches.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <TypeEmployeIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun résultat</p>
                    </div>
                  ) : (
                    employesAffiches.map((employe) => (
                      <div
                        key={employe.id}
                        onClick={() => setSelectedEmploye(employe.id.toString())}
                        className={`p-2 md:p-3 rounded-lg border cursor-pointer transition-all ${selectedEmploye === employe.id.toString()
                            ? 'border-[#246990] bg-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs md:text-sm font-medium truncate ${selectedEmploye === employe.id.toString() ? 'text-[#246990]' : 'text-gray-900'
                              }`}>
                              {employe.prenom} {employe.nom}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {getEmployeSecondaryLabel(employe)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                {nombrePages > 1 && (
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-3">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-2 md:px-3 py-1 text-xs md:text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-3 h-3 md:w-4 md:h-4" />
                    </button>

                    <span className="text-xs md:text-sm text-gray-600">
                      Page {currentPage} / {nombrePages}
                    </span>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(nombrePages, prev + 1))}
                      disabled={currentPage === nombrePages}
                      className="px-2 md:px-3 py-1 text-xs md:text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ========== COLONNE 2 : DÉTAILS ET GESTION ========== */}
            <div className="lg:col-span-6 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="p-3 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-4">
                <div className="p-2 bg-[#246990] rounded-full mr-2">
                  <Calculator className="text-white w-3 h-3 md:w-4 md:h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-[#246990] text-sm md:text-base truncate">Gestion des avances</h3>
                  <p className="text-xs text-gray-600 truncate">Créer et gérer les avances</p>
                </div>
              </div>

              {!selectedEmploye ? (
                <div className="text-center py-12 text-gray-400">
                  <CreditCard className="h-12 md:h-16 w-12 md:w-16 mx-auto mb-4 opacity-50" />
                  <h4 className="text-base md:text-lg font-medium text-gray-700 mb-2">Aucun employé sélectionné</h4>
                  <p className="text-xs md:text-sm">Sélectionnez un employé pour gérer ses avances</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Info employé */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-blue-900 text-sm md:text-base">
                          {getSelectedEmploye()?.prenom} {getSelectedEmploye()?.nom}
                        </div>
                        <div className="text-xs md:text-sm text-blue-700">
                          {getSelectedEmployeRoleLabel()}
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <div className="text-xs md:text-sm text-blue-600">
                          {typeConfig.referenceLabel}
                        </div>
                        <div className="text-lg md:text-xl font-bold text-blue-900">
                          {getSalaireReference().toLocaleString('fr-FR', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })} FCFA
                        </div>
                        {typeEmploye === 'HORAIRE' && (
                          <div className="text-xs text-blue-600">
                            {getSelectedEmploye()?.total_heures_mois || 0}h par mois
                          </div>
                        )}
                        {typeEmploye === 'HYBRIDE' && (
                          <div className="text-xs text-blue-600">
                            {getSelectedEmploye()?.composantes_count || getSelectedEmploye()?.composantes?.length || 0} composante(s)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 🆕 DÉTAIL CALCUL POUR ENSEIGNANTS HORAIRE */}
                  {typeEmploye === 'HORAIRE' && getSelectedEmploye()?.lignes_calcul?.length > 0 && (
                    <div className="border border-blue-200 rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-3 py-2 border-b border-blue-200">
                        <h4 className="text-xs md:text-sm font-medium text-blue-900">
                          Détail du calcul théorique
                        </h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr className="border-b border-gray-200">
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Classe</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">h/semaine</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">h/mois</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">Taux</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600 bg-blue-50">Montant</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {getSelectedEmploye().lignes_calcul.map((ligne, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium text-gray-900">
                                  {ligne.classe_nom}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-600">
                                  {ligne.heures_semaine.toFixed(1)}h
                                </td>
                                <td className="px-3 py-2 text-right text-blue-600 font-medium">
                                  {ligne.heures_mois.toFixed(1)}h
                                </td>
                                <td className="px-3 py-2 text-right text-gray-600">
                                  {ligne.taux_horaire.toLocaleString()} FCFA
                                </td>
                                <td className="px-3 py-2 text-right font-bold text-green-600 bg-blue-50">
                                  {Math.round(ligne.montant_theorique).toLocaleString()} FCFA
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            <tr>
                              <td colSpan="2" className="px-3 py-3 text-xs md:text-sm font-bold text-gray-900">
                                TOTAL MENSUEL
                              </td>
                              <td className="px-3 py-3 text-right text-xs md:text-sm font-bold text-blue-600">
                                {getSelectedEmploye().total_heures_mois}h
                              </td>
                              <td></td>
                              <td className="px-3 py-3 text-right text-base md:text-lg font-bold text-[#246990] bg-blue-50">
                                {getSelectedEmploye().salaire_theorique.toLocaleString()} FCFA
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {typeEmploye === 'HYBRIDE' && getSelectedEmploye()?.composantes?.length > 0 && (
                    <div className="border border-blue-200 rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-3 py-2 border-b border-blue-200">
                        <h4 className="text-xs md:text-sm font-medium text-blue-900">
                          Detail des composantes hybrides
                        </h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr className="border-b border-gray-200">
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Composante</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Mode</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">Brut</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">Retenues</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600 bg-blue-50">Net</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {getSelectedEmploye().composantes.map((composante, idx) => (
                              <tr key={composante.ligne_key || idx} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium text-gray-900">
                                  {composante.role || composante.categorie || 'Composante'}
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {composante.mode_remuneration || composante.type_personnel || '-'}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-700">
                                  {formatMontant(composante.salaire_brut)} FCFA
                                </td>
                                <td className="px-3 py-2 text-right text-orange-700">
                                  {formatMontant(composante.retenues_total)} FCFA
                                </td>
                                <td className="px-3 py-2 text-right font-bold text-[#246990] bg-blue-50">
                                  {formatMontant(composante.net_a_payer)} FCFA
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            <tr>
                              <td colSpan="2" className="px-3 py-3 text-xs md:text-sm font-bold text-gray-900">
                                TOTAL HYBRIDE
                              </td>
                              <td className="px-3 py-3 text-right text-xs md:text-sm font-bold text-gray-700">
                                {formatMontant(getSelectedEmploye().salaire_brut)} FCFA
                              </td>
                              <td className="px-3 py-3 text-right text-xs md:text-sm font-bold text-orange-700">
                                {formatMontant(getSelectedEmploye().retenues_total)} FCFA
                              </td>
                              <td className="px-3 py-3 text-right text-base md:text-lg font-bold text-[#246990] bg-blue-50">
                                {formatMontant(getSelectedEmploye().net_a_payer)} FCFA
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Sélection du mois */}
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-3">
                      Mois concerné *
                    </label>

                    {Object.entries(moisParTrimestre).map(([trimestre, mois]) => (
                      <div key={trimestre} className="mb-4">
                        <div className="text-xs font-semibold text-gray-500 mb-2">
                          TRIMESTRE {trimestre}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {mois.map(m => (
                            <button
                              key={m.id}
                              onClick={() => choisirMois(m)}
                              className={`p-2 md:p-3 rounded-lg border-2 transition-all text-left ${selectedMonth === m.id.toString()
                                  ? 'border-[#246990] bg-[#246990] text-white shadow-md'
                                  : 'border-gray-200 hover:border-[#246990]/50 hover:bg-gray-50'
                                }`}
                            >
                              <div className={`text-xs md:text-sm font-medium ${selectedMonth === m.id.toString() ? 'text-white' : 'text-gray-900'
                                }`}>
                                {m.nom}
                              </div>
                              <div className={`text-xs ${selectedMonth === m.id.toString() ? 'text-white/80' : 'text-gray-500'
                                }`}>
                                {m.annee_scolaire || '⚠️'}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* La consigne « choisir un mois » est dans la note du calendrier. */}
                  </div>

                  {/* Avances du mois sélectionné */}
                  {selectedMonth && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 md:px-4 py-3 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-2">
                        <h4 className="text-xs md:text-sm font-medium text-gray-700">
                          Avances pour {getSelectedMonth()?.nom} {formatAnneeScolaire(getSelectedMonth()?.annee_scolaire)}
                        </h4>
                        <button
                          onClick={handleOpenCreateModal}
                          disabled={saisieAvanceBloquee || isLimiteNombreAvancesAtteinte() || getDisponibleAvance() <= 0}
                          title={
                            saisieAvanceBloquee
                              ? motifBlocageAvance
                              : isLimiteNombreAvancesAtteinte()
                                ? `Limite atteinte : ${parametresSalaires.max_avances_salaire_mois} avance(s) maximum pour ce mois`
                                : getDisponibleAvance() <= 0
                                  ? "Plafond d'avance atteint pour ce mois"
                                  : 'Creer une nouvelle avance'
                          }
                          className="flex items-center justify-center px-3 py-1.5 bg-[#319190] text-white rounded-lg hover:bg-[#319190]/90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 text-xs whitespace-nowrap"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Nouvelle avance
                        </button>
                      </div>

                      {avancesMoisCourant.length === 0 ? (
                        <div className="p-6 text-center text-gray-400">
                          <CreditCard className="w-10 md:w-12 h-10 md:h-12 mx-auto mb-2 opacity-50" />
                          <p className="text-xs md:text-sm">Aucune avance pour ce mois</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {avancesMoisCourant.map((avance) => (
                            <div key={avance.id} className="p-3 md:p-4 hover:bg-gray-50">
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                                    <div className="text-base md:text-lg font-semibold text-[#246990]">
                                      {parseFloat(avance.montant).toLocaleString('fr-FR', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0
                                      })} FCFA
                                    </div>
                                    <BadgeStatutAvance statut={avance.statut} />
                                  </div>

                                  {avance.motif && (
                                    <div className="text-xs md:text-sm text-gray-600 mb-1">
                                      <span className="font-medium">Motif :</span> {avance.motif}
                                    </div>
                                  )}

                                  <div className="text-xs text-gray-500">
                                    Créée le {new Date(avance.date_demande).toLocaleDateString('fr-FR')}
                                    {avance.createur_nom && ` par ${avance.createur_nom}`}
                                  </div>

                                  {avance.justification_modification && (
                                    <div className="mt-2 p-2 bg-orange-50 rounded text-xs">
                                      <span className="font-medium text-orange-800">Modifiée :</span>
                                      <span className="text-orange-700 ml-1">{avance.justification_modification}</span>
                                    </div>
                                  )}
                                </div>

                                {avance.statut === 'validee' && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleOpenEditModal(avance)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                      title="Modifier"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleOpenDeleteModal(avance)}
                                      className="p-2 text-[#c66d59] hover:bg-red-50 rounded-lg"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                    {peutAnnulerPrivilegie && (
                                      <button
                                        onClick={() => {
                                          setAvancePrivilegiee(avance.id);
                                          setClePrivilegiee((cle) => cle + 1);
                                        }}
                                        className="p-2 text-[#246990] hover:bg-[#eef7fb] rounded-lg"
                                        title="Annulation privilégiée"
                                      >
                                        <ShieldAlert className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                )}

                                {avance.statut === 'appliquee' && (
                                  <div className="text-xs text-gray-500 flex items-center">
                                    <CheckCircle className="w-4 h-4 text-green-600 mr-1" />
                                    <span className="hidden md:inline">Déjà appliquée</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Total */}
                          <div className="p-3 md:p-4 bg-gray-50">
                            <div className="flex items-center justify-between">
                              <span className="text-sm md:text-base font-medium text-gray-700">Total des avances</span>
                              <span className="text-lg md:text-xl font-bold text-[#246990]">
                                {totalAvancesMois.toLocaleString('fr-FR', {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0
                                })} FCFA
                              </span>
                            </div>
                            <div className="mt-2 text-xs text-gray-600">
                              Disponible selon la regle : {formatMontant(getDisponibleAvance())} FCFA
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ========== COLONNE 3 : HISTORIQUE ========== */}
            <div className="lg:col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="p-3 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-4">
                <div className="p-2 bg-[#246990] rounded-full mr-2">
                  <History className="text-white w-3 h-3 md:w-4 md:h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-[#246990] text-sm md:text-base truncate">Historique</h3>
                  <p className="text-xs text-gray-600 truncate">Toutes les avances</p>
                </div>
              </div>

              {!selectedEmploye ? (
                <div className="text-center py-8 text-gray-400">
                  <History className="h-10 md:h-12 w-10 md:w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-xs md:text-sm">Sélectionnez un employé</p>
                </div>
              ) : historiqueAvances.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <History className="h-10 md:h-12 w-10 md:w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-xs md:text-sm">Aucune avance enregistrée</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] md:max-h-[600px] overflow-y-auto">
                  {historiqueAvances.map((avance) => (
                    <div
                      key={avance.id}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-900">
                          {avance.mois_nom} {formatAnneeScolaire(avance.annee_scolaire)}
                        </span>
                        <BadgeStatutAvance statut={avance.statut} />
                      </div>
                      <div className="text-base md:text-lg font-semibold text-[#246990] mb-1">
                        {parseFloat(avance.montant).toLocaleString('fr-FR', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })} FCFA
                      </div>
                      {avance.motif && (
                        <div className="text-xs text-gray-600 mb-1 line-clamp-2">
                          {avance.motif}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        {new Date(avance.date_demande).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ========== MODALS ========== */}

      {/* Nouvelle avance */}
      <Dialog open={showCreateModal} onOpenChange={(ouvert) => { if (!ouvert && !isCreatingAvance) setShowCreateModal(false); }}>
        <DialogContent className={`${CONTENU_DIALOG} max-w-4xl`}>
          <DialogHeader className={ENTETE_DIALOG}>
            <PastilleDialog icone={HandCoins} />
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Nouvelle avance sur salaire</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {getSelectedEmploye()?.prenom} {getSelectedEmploye()?.nom} · {getSelectedMonth()?.nom} {formatAnneeScolaire(getSelectedMonth()?.annee_scolaire)}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="grid flex-1 gap-4 overflow-y-auto bg-gray-50 px-5 py-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
              <label className="block">
                <span className={LIBELLE}>Montant de l'avance</span>
                <div className="relative">
                  <input
                    type="number"
                    value={montantAvance}
                    onChange={(e) => setMontantAvance(e.target.value)}
                    placeholder="Ex : 50000"
                    className={`${CHAMP} pr-14`}
                    min="0"
                    max={getDisponibleAvance()}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
                </div>
                <span className="mt-1 block text-[11px] text-gray-500">
                  Maximum disponible selon la règle : {formatMontant(getDisponibleAvance())} FCFA
                </span>
              </label>

              <label className="block">
                <span className={LIBELLE}>Motif (facultatif)</span>
                <textarea
                  value={motifAvance}
                  onChange={(e) => setMotifAvance(e.target.value)}
                  placeholder="Raison de l'avance..."
                  className={ZONE_TEXTE}
                  rows={4}
                />
              </label>
            </div>

            <aside className="space-y-3">
              <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{typeConfig.referenceLabel}</p>
                <p className="mt-1 text-lg font-semibold text-[#246990]">{formatMontant(getSalaireReference())} FCFA</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <p className="text-xs text-gray-500">Plafond autorisé</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{formatMontant(getPlafondAvance())} FCFA</p>
                  <p className="text-[11px] text-gray-500">{parametresSalaires.pourcentage_max_avance_salaire} % du salaire</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <p className="text-xs text-gray-500">Avances du mois</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {getNombreAvancesMois()} / {parametresSalaires.max_avances_salaire_mois}
                  </p>
                </div>
                <div className="rounded-md border border-orange-200 bg-orange-50 p-3">
                  <p className="text-xs text-orange-700">Déjà avancé</p>
                  <p className="mt-1 text-sm font-semibold text-orange-800">{formatMontant(totalAvancesMois)} FCFA</p>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-xs text-emerald-700">Disponible</p>
                  <p className="mt-1 text-sm font-semibold text-emerald-800">{formatMontant(getDisponibleAvance())} FCFA</p>
                </div>
              </div>
            </aside>
          </div>

          <DialogFooter className={PIED_DIALOG}>
            <button type="button" onClick={() => setShowCreateModal(false)} disabled={isCreatingAvance} className={BOUTON_SECONDAIRE}>
              Annuler
            </button>
            <button
              type="button"
              onClick={handleCreateAvance}
              disabled={isCreatingAvance || isLimiteNombreAvancesAtteinte() || getDisponibleAvance() <= 0}
              className={BOUTON_PRINCIPAL}
            >
              {isCreatingAvance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {isCreatingAvance ? 'Création...' : "Créer l'avance"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modifier l'avance */}
      <Dialog open={showEditModal && Boolean(avanceToEdit)} onOpenChange={(ouvert) => { if (!ouvert && !isEditingAvance) setShowEditModal(false); }}>
        <DialogContent className={`${CONTENU_DIALOG} max-w-2xl`}>
          <DialogHeader className={ENTETE_DIALOG}>
            <PastilleDialog icone={Edit2} />
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Modifier l'avance</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Montant d'origine : {formatMontant(avanceToEdit?.montant)} FCFA · la modification est justifiée et tracée.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className={LIBELLE}>Montant</span>
                <div className="relative">
                  <input
                    type="number"
                    value={montantEdit}
                    onChange={(e) => setMontantEdit(e.target.value)}
                    className={`${CHAMP} pr-14`}
                    min="0"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
                </div>
              </label>
              <label className="block">
                <span className={LIBELLE}>Motif</span>
                <input
                  type="text"
                  value={motifEdit}
                  onChange={(e) => setMotifEdit(e.target.value)}
                  className={CHAMP}
                />
              </label>
            </div>

            <label className="block">
              <span className={LIBELLE}>Justification de la modification</span>
              <textarea
                value={justificationEdit}
                onChange={(e) => setJustificationEdit(e.target.value)}
                placeholder="Expliquez pourquoi vous modifiez cette avance..."
                className={ZONE_TEXTE}
                rows={3}
              />
            </label>
          </div>

          <DialogFooter className={PIED_DIALOG}>
            <button type="button" onClick={() => setShowEditModal(false)} disabled={isEditingAvance} className={BOUTON_SECONDAIRE}>
              Annuler
            </button>
            <button type="button" onClick={handleEditAvance} disabled={isEditingAvance} className={BOUTON_PRINCIPAL}>
              {isEditingAvance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {isEditingAvance ? 'Modification...' : 'Enregistrer'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Annuler l'avance */}
      <Dialog open={showDeleteModal && Boolean(avanceToDelete)} onOpenChange={(ouvert) => { if (!ouvert && !isDeletingAvance) setShowDeleteModal(false); }}>
        <DialogContent className={`${CONTENU_DIALOG} max-w-2xl`}>
          <DialogHeader className={ENTETE_DIALOG}>
            <PastilleDialog icone={Trash2} danger />
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Annuler l'avance</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                La sortie de caisse liée est reprise ; l'annulation est justifiée et tracée.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
              <p className="text-sm text-red-800">
                Avance de <span className="font-semibold">{formatMontant(avanceToDelete?.montant)} FCFA</span> pour{' '}
                {getSelectedEmploye()?.prenom} {getSelectedEmploye()?.nom},{' '}
                {getSelectedMonth()?.nom} {formatAnneeScolaire(getSelectedMonth()?.annee_scolaire)}.
              </p>
            </div>

            <label className="block">
              <span className={LIBELLE}>Justification de l'annulation</span>
              <textarea
                value={justificationDelete}
                onChange={(e) => setJustificationDelete(e.target.value)}
                placeholder="Expliquez pourquoi vous annulez cette avance..."
                className={ZONE_TEXTE}
                rows={4}
              />
            </label>
          </div>

          <DialogFooter className={PIED_DIALOG}>
            <button type="button" onClick={() => setShowDeleteModal(false)} disabled={isDeletingAvance} className={BOUTON_SECONDAIRE}>
              Retour
            </button>
            <button type="button" onClick={handleDeleteAvance} disabled={isDeletingAvance} className={BOUTON_DANGER}>
              {isDeletingAvance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {isDeletingAvance ? 'Annulation...' : "Confirmer l'annulation"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AnnulationPrivilegieeDialog
        key={clePrivilegiee}
        avanceId={avancePrivilegiee}
        open={Boolean(avancePrivilegiee)}
        onClose={() => setAvancePrivilegiee(null)}
        onEnvoyee={(resultat) => {
          setAvancePrivilegiee(null);
          showToast(resultat?.message || 'Demande envoyée au Centre de Support', 'success');
        }}
      />

      <JournalActionsPrivilegiees open={journalOuvert} onClose={() => setJournalOuvert(false)} />

    </div>
  );
};

export default AvancesSalaire;



